// ============================================================
// useOnboardingPrompt.ts
// Decides whether a given case-detail section should show its
// one-time onboarding prompt.
//
// Eligibility is snapshotted once per section mount via useRef so
// the prompt doesn't flicker out mid-render when ViewingPane's
// "mark prompts seen" effect lands. After the user starts typing,
// `sectionEmpty` flips to false and the prompt hides naturally.
// ============================================================
import { useRef } from 'react'
import { useCasevoStore } from '../store/useCasevoStore'

export const ONBOARDING_PROMPTS_SEEN_KEY = 'casevo_onboarding_prompts_seen'

/**
 * @param sectionEmpty true when every relevant field on currentCase is
 *                     null/empty/undefined for this section
 * @returns true when the small italic onboarding prompt should render
 */
export const useShouldShowOnboardingPrompt = (sectionEmpty: boolean): boolean => {
  const firmCaseCount = useCasevoStore((s) => s.firmCaseCount)
  // Read localStorage + count exactly once at mount. After ViewingPane's
  // mount effect sets the localStorage flag, subsequent renders here
  // continue to use this captured value, so the user doesn't see the
  // prompt vanish on the next state change.
  const eligibleAtMountRef = useRef<boolean | null>(null)
  if (eligibleAtMountRef.current === null) {
    eligibleAtMountRef.current =
      typeof window !== 'undefined' &&
      window.localStorage.getItem(ONBOARDING_PROMPTS_SEEN_KEY) === null &&
      firmCaseCount === 1
  }
  return eligibleAtMountRef.current && sectionEmpty
}
